import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

export type AppLanguage = 'es' | 'en';

const resources = {
  es: {
    translation: {
      appName: 'Eclipse 2026',
      subtitle: 'España · 12 de agosto',
      language: 'English',
      openLayers: 'Capas',
      close: 'Cerrar',
      layers: {
        title: 'Capas del mapa',
        totality: 'Franja de totalidad',
        obscuration: 'Sombra de ocultación solar',
        clouds: 'Nubosidad',
        traffic: 'Tráfico en directo',
        terrain: 'Relieve 3D',
      },
      countdown: {
        title: 'Hasta el eclipse',
        days: 'd',
        hours: 'h',
        minutes: 'min',
        seconds: 's',
        complete: 'El eclipse ya ha comenzado',
      },
      time: {
        preview: 'Vista previa',
        eclipseForecast: 'Hora del eclipse',
        selectPoint: 'Elige un punto para ver sus fases locales',
      },
      map: {
        label: 'Mapa interactivo del eclipse',
        selectPrompt: 'Elige un punto para comprobar el eclipse',
        loading: 'Cargando mapa…',
        error: 'No se pudo cargar el mapa base. Comprueba la conexión y vuelve a intentarlo.',
        originPrompt: 'Toca el mapa para elegir el origen',
      },
      location: {
        title: 'Punto seleccionado',
        total: 'Eclipse total',
        partial: 'Eclipse parcial',
        none: 'Fuera del eclipse visible',
        magnitude: 'Magnitud',
        obscuration: 'Sol oculto',
        obscurationAtTime: 'Sol oculto',
        totality: 'Totalidad',
        totalitySeconds: 'segundos de totalidad',
        maximum: 'Máximo',
        contacts: 'Contactos',
        conditions: 'Condiciones de observación',
        duration: '{{value}} min',
        seconds: '{{value}} s',
        altitude: 'Altura del Sol al máximo',
        azimuth: 'Azimut',
        elevation: 'Elevación',
        coordinates: 'Coordenadas',
        phase_before: 'Aún no ha comenzado',
        phase_partial: 'Fase parcial',
        phase_total: 'Totalidad',
        phase_after: 'Finalizado',
        contact_c1: 'Comienza',
        contact_c2: 'Totalidad',
        contact_maximum: 'Máximo',
        contact_c3: 'Termina total',
        contact_c4: 'Finaliza',
      },
      weather: {
        title: 'Nubosidad estimada',
        forecast: 'Previsión para el eclipse',
        forecastDate: 'Disponible desde ~7 ago',
        unavailable: 'La previsión de alta resolución aparecerá unos cinco días antes.',
        loading: 'Actualizando nubosidad…',
        error: 'No se ha podido cargar la nubosidad.',
        cover: '{{value}} % de nubes',
        modelNote: 'Estimación de modelo, no imagen de satélite.',
        updated: 'Modelo: {{time}}',
        legend: 'Nubosidad ICON‑EU',
      },
      terrain: {
        title: 'Visibilidad por relieve',
        atMaximum: 'Relieve al máximo',
        checking: 'Calculando el horizonte…',
        clear: 'Horizonte despejado',
        blocked: 'El relieve puede ocultar el Sol',
        uncertain: 'Visibilidad incierta',
        error: 'No se pudo analizar el relieve.',
        horizon: 'Horizonte del terreno: {{value}}°',
        note: 'No incluye edificios, árboles ni cambios recientes del terreno.',
      },
      route: {
        title: 'Planificar viaje',
        origin: 'Origen',
        useLocation: 'Usar mi ubicación',
        pickOrigin: 'Elegir en el mapa',
        originReady: 'Origen seleccionado',
        permissionDenied: 'No se pudo acceder a tu ubicación.',
        departure: 'Salida',
        departureTime: 'Fecha y hora de salida',
        spainTime: 'Hora peninsular española · CEST',
        now: 'Ahora',
        planned: 'Planificada',
        calculate: 'Calcular ruta',
        calculating: 'Calculando…',
        distance: '{{value}} km',
        eta: '{{value}} de viaje',
        delay: '{{value}} min de retraso por tráfico',
        live: 'Tráfico en directo',
        historical: 'Tráfico habitual',
        exceptionalWarning:
          'La estimación previa usa tráfico habitual y no incluye la demanda excepcional del eclipse. Sal con mucho margen.',
        unavailable: 'La ruta con tráfico no está disponible ahora.',
        quota: 'Se ha alcanzado temporalmente el límite gratuito de tráfico.',
        notConfigured: 'El despliegue aún no tiene configurada la clave de tráfico.',
        outside: 'El origen y el destino deben estar en España o Baleares.',
        invalidDeparture: 'Elige una fecha y hora válidas.',
      },
      traffic: {
        quotaNote: 'Capa opcional; consume el cupo gratuito solo al activarla.',
        unavailable: 'Tráfico no disponible',
      },
      info: {
        open: 'Fuentes y privacidad',
        title: 'Fuentes, límites y privacidad',
        privacy:
          'Sin cuentas, cookies analíticas ni seguimiento. Tu ubicación precisa solo se envía al calcular una ruta y no se almacena.',
        free: 'Los servicios gratuitos tienen límites. Si se agota el tráfico, el eclipse, el relieve y la meteorología siguen funcionando.',
        weatherLicense:
          'La nubosidad procede del modelo abierto ICON-EU del Servicio Meteorológico Alemán (DWD), bajo CC BY 4.0.',
        sources: 'Datos y atribuciones',
        calculation:
          'Los límites reales pueden variar unos segundos por el perfil del borde lunar. Comprueba siempre el horizonte en persona.',
        obscurationModel:
          'La sombra animada representa ocultación solar calculada con elementos besselianos, no el brillo ambiental percibido. La atmósfera y el relieve pueden cambiar cómo se ve.',
        safety:
          'Seguridad: nunca mires directamente al Sol sin filtros homologados ISO 12312-2. Solo es seguro retirar la protección durante la totalidad completa y únicamente desde dentro de la franja de totalidad.',
      },
      common: {
        retry: 'Reintentar',
        unavailable: 'No disponible',
      },
    },
  },
  en: {
    translation: {
      appName: 'Eclipse 2026',
      subtitle: 'Spain · 12 August',
      language: 'Español',
      openLayers: 'Layers',
      close: 'Close',
      layers: {
        title: 'Map layers',
        totality: 'Path of totality',
        obscuration: 'Solar obscuration shadow',
        clouds: 'Cloud cover',
        traffic: 'Live traffic',
        terrain: '3D terrain',
      },
      countdown: {
        title: 'Until the eclipse',
        days: 'd',
        hours: 'h',
        minutes: 'min',
        seconds: 's',
        complete: 'The eclipse has started',
      },
      time: {
        preview: 'Preview',
        eclipseForecast: 'Eclipse time',
        selectPoint: 'Select a point to see its local phases',
      },
      map: {
        label: 'Interactive eclipse map',
        selectPrompt: 'Select a point to check the eclipse',
        loading: 'Loading map…',
        error: 'The base map could not load. Check the connection and try again.',
        originPrompt: 'Tap the map to choose your origin',
      },
      location: {
        title: 'Selected location',
        total: 'Total eclipse',
        partial: 'Partial eclipse',
        none: 'Outside the visible eclipse',
        magnitude: 'Magnitude',
        obscuration: 'Sun covered',
        obscurationAtTime: 'Sun covered',
        totality: 'Totality',
        totalitySeconds: 'seconds of totality',
        maximum: 'Maximum',
        contacts: 'Contacts',
        conditions: 'Viewing conditions',
        duration: '{{value}} min',
        seconds: '{{value}} s',
        altitude: 'Sun altitude at maximum',
        azimuth: 'Azimuth',
        elevation: 'Elevation',
        coordinates: 'Coordinates',
        phase_before: 'Not started yet',
        phase_partial: 'Partial phase',
        phase_total: 'Totality',
        phase_after: 'Finished',
        contact_c1: 'Begins',
        contact_c2: 'Totality',
        contact_maximum: 'Maximum',
        contact_c3: 'Totality ends',
        contact_c4: 'Finishes',
      },
      weather: {
        title: 'Estimated cloud cover',
        forecast: 'Eclipse forecast',
        forecastDate: 'Available from ~7 Aug',
        unavailable: 'The high-resolution forecast will appear around five days beforehand.',
        loading: 'Updating cloud cover…',
        error: 'Cloud cover could not be loaded.',
        cover: '{{value}}% cloud cover',
        modelNote: 'Model estimate, not satellite imagery.',
        updated: 'Model: {{time}}',
        legend: 'ICON‑EU clouds',
      },
      terrain: {
        title: 'Terrain visibility',
        atMaximum: 'Terrain at maximum',
        checking: 'Calculating the horizon…',
        clear: 'Clear terrain horizon',
        blocked: 'Terrain may block the Sun',
        uncertain: 'Visibility is uncertain',
        error: 'Terrain could not be analysed.',
        horizon: 'Terrain horizon: {{value}}°',
        note: 'Buildings, trees and recent terrain changes are not included.',
      },
      route: {
        title: 'Plan a trip',
        origin: 'Origin',
        useLocation: 'Use my location',
        pickOrigin: 'Choose on map',
        originReady: 'Origin selected',
        permissionDenied: 'Your location could not be accessed.',
        departure: 'Departure',
        departureTime: 'Departure date and time',
        spainTime: 'Mainland Spain time · CEST',
        now: 'Now',
        planned: 'Planned',
        calculate: 'Calculate route',
        calculating: 'Calculating…',
        distance: '{{value}} km',
        eta: '{{value}} journey',
        delay: '{{value}} min traffic delay',
        live: 'Live traffic',
        historical: 'Typical traffic',
        exceptionalWarning:
          'Advance estimates use typical traffic and do not include exceptional eclipse demand. Leave plenty of extra time.',
        unavailable: 'Traffic-aware routing is currently unavailable.',
        quota: 'The free traffic allowance has temporarily been reached.',
        notConfigured: 'This deployment has not configured its traffic key yet.',
        outside: 'The origin and destination must be within Spain or the Balearics.',
        invalidDeparture: 'Choose a valid date and time.',
      },
      traffic: {
        quotaNote: 'Optional layer; it uses the free allowance only when enabled.',
        unavailable: 'Traffic unavailable',
      },
      info: {
        open: 'Sources and privacy',
        title: 'Sources, limits and privacy',
        privacy:
          'No accounts, analytics cookies or tracking. Your precise location is sent only when calculating a route and is never stored.',
        free: 'Free services have quotas. If traffic is exhausted, eclipse, terrain and weather features keep working.',
        weatherLicense:
          'Cloud cover comes from the German Weather Service (DWD) open ICON-EU model under CC BY 4.0.',
        sources: 'Data and attribution',
        calculation:
          'Actual limits may vary by a few seconds because of the lunar limb profile. Always inspect the horizon in person.',
        obscurationModel:
          'The animated shadow represents solar obscuration calculated from Besselian elements, not perceived ambient brightness. Atmosphere and terrain can change how it looks.',
        safety:
          'Safety: never look directly at the Sun without compliant ISO 12312-2 filters. Eye protection is removed only during complete totality and only from inside the path of totality.',
      },
      common: {
        retry: 'Retry',
        unavailable: 'Unavailable',
      },
    },
  },
} as const;

function storedLanguage(): string | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage.getItem('eclipse-language');
  } catch {
    return null;
  }
}

const savedLanguage = storedLanguage();
const initialLanguage: AppLanguage =
  savedLanguage === 'es' || savedLanguage === 'en'
    ? savedLanguage
    : typeof navigator !== 'undefined' && navigator.language.toLowerCase().startsWith('es')
      ? 'es'
      : 'en';

void i18n.use(initReactI18next).init({
  resources,
  lng: initialLanguage,
  fallbackLng: 'es',
  showSupportNotice: false,
  interpolation: { escapeValue: false },
});

if (typeof document !== 'undefined') document.documentElement.lang = initialLanguage;

export async function changeLanguage(language: AppLanguage): Promise<void> {
  await i18n.changeLanguage(language);
  try {
    if (typeof localStorage !== 'undefined') localStorage.setItem('eclipse-language', language);
  } catch {
    // Storage can be unavailable in strict privacy modes; language still changes in memory.
  }
  if (typeof document !== 'undefined') document.documentElement.lang = language;
}

export const translationResources = resources;
export default i18n;
